import { Component } from '@theme/component';
import { CartAddEvent, ThemeEvents } from '@theme/events';
import { fetchConfig, onAnimationEnd } from '@theme/utilities';

/**
 * @typedef {Object} VariantData
 * @property {number} id - Variant ID
 * @property {string} title - Variant title
 * @property {boolean} available - Whether variant is available
 * @property {number} inventory_quantity - Inventory quantity
 * @property {string} inventory_management - Inventory management type
 * @property {string[]} options - Variant options
 * @property {number} price - Variant price
 * @property {{preview_image: {src: string}}|null} [featured_media] - Featured media for variant
 */

/**
 * @typedef {Object} OptionData
 * @property {string} name - Option name
 * @property {number} position - Option position
 * @property {string[]} values - Option values
 */

/**
 * @typedef {Object} BuyMoreSaveMoreRefs
 * @property {HTMLElement} tiersContainer - Container for tier options
 * @property {HTMLInputElement} tierRadio2 - Radio for tier 2
 * @property {HTMLInputElement} tierRadio3 - Radio for tier 3
 * @property {HTMLInputElement} tierRadio5 - Radio for tier 5
 * @property {HTMLElement} tier2 - Tier 2 container
 * @property {HTMLElement} tier3 - Tier 3 container
 * @property {HTMLElement} tier5 - Tier 5 container
 * @property {HTMLElement} variantsContainer2 - Variants container for tier 2
 * @property {HTMLElement} variantsContainer3 - Variants container for tier 3
 * @property {HTMLElement} variantsContainer5 - Variants container for tier 5
 * @property {HTMLScriptElement} variantsData - JSON data for variants
 * @property {HTMLScriptElement} optionsData - JSON data for options
 * @property {HTMLScriptElement} variantInventoryData - JSON data for variant inventory
 */

/**
 * Buy More Save More component for product pages
 * Handles tiered discount offers with variant selection
 *
 * @extends {Component<BuyMoreSaveMoreRefs>}
 */
export class BuyMoreSaveMoreComponent extends Component {
  /** @type {VariantData[]} */
  #variants = [];

  /** @type {OptionData[]} */
  #options = [];

  /** @type {Map<number, {inventory: number, available: boolean, inventory_management: string}>} */
  #variantInventory = new Map();

  /** @type {number} */
  #currentTier = 3;

  /** @type {number} */
  #totalInventory = 0;

  /** @type {Object<number, Object<number, Object<number, string>>>} */
  #selections = {};

  /** @type {number[]} */
  #availableTiers = [];

  /** @type {Map<number, {discount: number, price: number}>} */
  #tierData = new Map();

  /** @type {boolean} */
  #hasManuallyInteracted = false;

  /** @type {boolean} */
  #hasAutoFilled = false;

  /** @type {boolean} */
  #hasVariants = false;

  /** @type {boolean} */
  #hasSizeOption = false;

  /** @type {number|null} */
  #sizeOptionIndex = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {Array<{button: HTMLButtonElement, handler: (e: Event) => void}>} */
  #addToCartListeners = [];

  /** @type {number[]} */
  #resetTimeouts = [];

  /** @type {number} */
  #basePrice = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#initializeData();
    this.#initializePricing();
    this.#listenToVariantEvents();
    
    // Ensure Buy 3 is preselected on load
    this.#ensureInitialTierSelection();
    
    // Set up add to cart button listeners (direct listeners to avoid component nesting issues)
    this.#setupAddToCartListeners();
  }

  /**
   * Ensure initial tier selection based on available inventory
   */
  #ensureInitialTierSelection() {
    // Find the checked radio button (set by Liquid)
    const tierRadio = this.querySelector(`input[type="radio"][name*="bmsm-tier"]:checked`);

    if (!(tierRadio instanceof HTMLInputElement)) return;

    const tierValue = parseInt(tierRadio.value, 10);
    this.#currentTier = tierValue;

    // Always update UI and availability on initial load
    this.#updateTierUI(tierValue);
    this.#buildVariantDropdowns(tierValue);

    // Use setTimeout to ensure DOM is fully ready
    setTimeout(() => {
      this.#updateVariantAvailability();
      this.#updateButtonState(tierValue);
      // Re-setup listeners in case buttons were just rendered
      this.#setupAddToCartListeners();
    }, 0);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * Initialize variant and options data from JSON
   */
  #initializeData() {
    // Parse variants data
    const variantsDataEl = this.refs.variantsData;
    if (variantsDataEl?.textContent) {
      try {
        this.#variants = JSON.parse(variantsDataEl.textContent);
      } catch (error) {
        console.error('Failed to parse variants data:', error);
      }
    } 

    // Parse options data
    const optionsDataEl = this.refs.optionsData;
    if (optionsDataEl?.textContent) {
      try {
        this.#options = JSON.parse(optionsDataEl.textContent);
      } catch (error) {
        console.error('Failed to parse options data:', error);
      }
    }

    // Parse variant inventory data (pre-calculated in Liquid)
    const inventoryDataEl = this.refs.variantInventoryData;
    if (inventoryDataEl?.textContent) {
      try {
        const inventoryData = JSON.parse(inventoryDataEl.textContent);
        // Build inventory map for quick lookup and calculate total inventory
        this.#variantInventory.clear();
        this.#totalInventory = 0;

        for (const item of inventoryData) {
          this.#variantInventory.set(item.id, {
            inventory: item.inventory,
            available: item.available,
            inventory_management: item.inventory_management,
          });

          if (item.available && item.inventory > 0) {
            this.#totalInventory += item.inventory;
          }
        }
      } catch (error) {
        console.error('Failed to parse variant inventory data:', error);
      }
    }

    // Get configuration from data attributes
    this.#hasVariants = this.dataset.hasVariants === 'true';
    this.#hasSizeOption = this.dataset.hasSizeOption === 'true';
    this.#sizeOptionIndex = this.dataset.sizeOptionIndex ? parseInt(this.dataset.sizeOptionIndex, 10) : null;
    this.#totalInventory = parseInt(this.dataset.totalInventory || '0', 10) || this.#totalInventory;
    
    // Get base price (product default price)
    this.#basePrice = parseInt(this.dataset.basePrice || '0', 10);

    // Parse dynamic tier data
    // Format: "quantity:discount:price_cents,quantity:discount:price_cents"
    // Note: Already sorted in metafield, no need to sort again
    const tiersDataStr = this.dataset.tiersData || '';
    if (tiersDataStr) {
      const tierParts = tiersDataStr.split(',');
      for (const tierPart of tierParts) {
        const [qtyStr, discountStr, priceStr] = tierPart.split(':');
        const qty = parseInt(qtyStr, 10);
        const discount = parseInt(discountStr, 10);
        const price = parseInt(priceStr, 10);
        
        if (!isNaN(qty) && !isNaN(discount) && !isNaN(price)) {
          this.#availableTiers.push(qty);
          this.#tierData.set(qty, { discount, price });
        }
      }
    }

    // Get initial tier
    const initialTierStr = this.dataset.initialTier;
    if (initialTierStr) {
      this.#currentTier = parseInt(initialTierStr, 10);
    }

    // Initialize selections for all available tiers
    this.#initializeSelections();

    // Update button states
    this.#updateAllButtonStates();

    // Initialize custom dropdowns for first option
    this.#initializeCustomDropdowns();
  }

  /**
   * Initialize pricing on page load
   * Gets currently selected variant and updates prices accordingly
   */
  #initializePricing() {
    // Get currently selected variant
    const selectedVariant = this.#getCurrentSelectedVariant();
    
    if (selectedVariant && selectedVariant.available) {
      // Use variant price if available
      this.#updatePricesForBasePrice(selectedVariant.price);
    } else {
      // Use product default price if variant is OOS or not selected
      this.#updatePricesForBasePrice(this.#basePrice);
    }
  }

  /**
   * Get currently selected variant from PDP
   * @returns {VariantData|null}
   */
  #getCurrentSelectedVariant() {
    // Try to get variant from URL parameter
    const urlParams = new URLSearchParams(window.location.search);
    const variantIdFromUrl = urlParams.get('variant');
    
    if (variantIdFromUrl) {
      const variant = this.#variants.find(v => String(v.id) === String(variantIdFromUrl));
      if (variant) return variant;
    }

    // Try to get variant from product form
    const productForm = document.querySelector('product-form-component');
    if (productForm) {
      const variantIdInput = productForm.querySelector('input[name="id"], input[ref="variantId"]');
      if (variantIdInput instanceof HTMLInputElement && variantIdInput.value) {
        const variant = this.#variants.find(v => String(v.id) === String(variantIdInput.value));
        if (variant) return variant;
      }
    }

    // Try to get from selected variant picker
    const selectedInput = document.querySelector('variant-picker input[type="radio"]:checked, variant-picker select option:checked');
    if (selectedInput) {
      const variantId = selectedInput instanceof HTMLInputElement 
        ? selectedInput.dataset.variantId 
        : selectedInput instanceof HTMLOptionElement 
        ? selectedInput.dataset.variantId 
        : null;
      
      if (variantId) {
        const variant = this.#variants.find(v => String(v.id) === String(variantId));
        if (variant) return variant;
      }
    }

    // Fallback: get first available variant
    return this.#variants.find(v => v.available) || this.#variants[0] || null;
  }

  /**
   * Update prices based on base price
   * @param {number} basePrice - Base price in cents
   */
  #updatePricesForBasePrice(basePrice) {
    if (!basePrice || basePrice <= 0) {
      basePrice = this.#basePrice;
    }

    // Ensure base price is valid (at least 1 cent)
    if (basePrice < 1) {
      basePrice = this.#basePrice || 100; // Fallback to 100 cents ($1.00) if base price is invalid
    }

    // Update prices for all available tiers
    for (const tier of this.#availableTiers) {
      const tierInfo = this.#tierData.get(tier);
      if (!tierInfo) continue;

      const discount = tierInfo.discount;
      const tierMultiplier = (100 - discount) / 100;
      const tierPrice = Math.max(1, Math.round(basePrice * tierMultiplier));

      this.#updateTierPriceDisplay(tier, tierPrice);
    }
  }

  /**
   * Update price display for a specific tier
   * @param {number} tier - Tier number (2, 3, or 5)
   * @param {number} priceCents - Price in cents
   */
  #updateTierPriceDisplay(tier, priceCents) {
    const tierElement = this.querySelector(`.bmsm__tier[data-tier="${tier}"]`);
    if (!tierElement) return;

    const priceAmountEl = tierElement.querySelector('.bmsm__price-amount');
    if (priceAmountEl) {
      // Format price using Shopify's money format
      const formattedPrice = this.#formatMoney(priceCents);
      priceAmountEl.textContent = formattedPrice;
    }

    // Update add to cart button text if it exists
    const addToCartButton = tierElement.querySelector(`button.bmsm__add-to-cart[data-tier="${tier}"]`);
    if (addToCartButton) {
      const cartTextEl = addToCartButton.querySelector('[data-role="add-to-cart-text"]');
      if (cartTextEl) {
        const formattedPrice = this.#formatMoney(priceCents);
        // Try to get the translation key, fallback to default format
        const buttonText = cartTextEl.textContent || '';
        if (buttonText.includes('Add to Cart')) {
          cartTextEl.textContent = `Add to Cart - ${formattedPrice} per piece`;
        } else {
          // Preserve existing format if it exists
          cartTextEl.textContent = cartTextEl.textContent.replace(/\$[\d,]+\.?\d*/g, formattedPrice);
        }
      }
    }
  }

  /**
   * Format money amount (simple formatter, can be enhanced)
   * @param {number} cents - Price in cents
   * @returns {string} Formatted price string
   */
  #formatMoney(cents) {
    // Use Shopify's money format if available
    const shopify = /** @type {any} */ (window.Shopify);
    if (shopify && typeof shopify.formatMoney === 'function') {
      return shopify.formatMoney(cents);
    }
    
    // Fallback formatting
    const dollars = (cents / 100).toFixed(2);
    return `$${dollars}`;
  }

  /**
   * Calculate combined price for selected variants in a tier
   * @param {number} tier - Tier number
   * @returns {number|null} Combined price in cents or null if not all variants selected
   */
  #calculateCombinedPriceForTier(tier) {
    if (!this.#hasVariants) {
      // Single variant product - combined price is basePrice multiplied by tier quantity.
      // Caller (#updatePricesForSelectedVariants) divides by tier to derive the per-piece
      // price, so returning just basePrice here would incorrectly shrink the price by the
      // tier count on tier changes (e.g. Buy 2 -> Buy 3). Multiplying keeps semantics
      // consistent with the multi-variant branch which returns the sum across all rows.
      return this.#basePrice * tier;
    }

    // Check if all selections are complete
    let totalPrice = 0;
    let allComplete = true;

    for (let row = 1; row <= tier; row++) {
      const variant = this.#findVariantFromSelection(tier, row);
      if (!variant) {
        allComplete = false;
        break;
      }
      totalPrice += variant.price || 0;
    }

    if (!allComplete) {
      return null;
    }

    return totalPrice;
  }

  /**
   * Update prices when ALL variants are selected in a tier
   * Only updates price if all variants for the tier are selected
   * @param {number} tier - Tier number
   */
  #updatePricesForSelectedVariants(tier) {
    const combinedPrice = this.#calculateCombinedPriceForTier(tier);
    
    // Only update price if ALL variants are selected
    if (combinedPrice === null) {
      // Not all variants selected - don't update prices
      return;
    }

    // All variants selected - calculate average price per piece from combined
    const priceToUse = Math.round(combinedPrice / tier);

    // Get discount for this tier
    const tierInfo = this.#tierData.get(tier);
    if (!tierInfo) return;

    const discount = tierInfo.discount;
    const discountMultiplier = (100 - discount) / 100;

    // Ensure price is valid (at least 1 cent) and apply discount
    const discountedPrice = Math.max(1, Math.round(priceToUse * discountMultiplier));

    // Update only the current tier's price
    this.#updateTierPriceDisplay(tier, discountedPrice);
  }

  /**
   * Initialize empty selections for all available tiers
   */
  #initializeSelections() {
    for (const tier of this.#availableTiers) {
      this.#selections[tier] = {};
      for (let row = 1; row <= tier; row++) {
        this.#selections[tier][row] = {};
        for (let optIdx = 0; optIdx < this.#options.length; optIdx++) {
          this.#selections[tier][row][optIdx] = '';
        }
      }
    }
  }

  /**
   * Initialize custom dropdowns for all options
   */
  #initializeCustomDropdowns() {
    const customDropdowns = this.querySelectorAll('.bmsm__custom-dropdown-wrapper');

      for (const wrapper of customDropdowns) {
      const button = wrapper.querySelector('.bmsm__custom-dropdown-button');
      const list = wrapper.querySelector('.bmsm__custom-dropdown-list');
      const input = wrapper.querySelector('.bmsm__custom-dropdown-input');

      if (!(button instanceof HTMLButtonElement) || !(list instanceof HTMLUListElement) || !(input instanceof HTMLInputElement)) continue;

      // Handle button click to toggle dropdown
      button.addEventListener('click', (e) => {
        e.stopPropagation();
        this.#toggleCustomDropdown(button, list);
      });

      // Handle item clicks
      const items = list.querySelectorAll('.bmsm__custom-dropdown-item');

      for (const item of items) {
        item.addEventListener('click', (e) => {
          e.stopPropagation();

          if (item.classList.contains('bmsm__custom-dropdown-item--disabled')) return;

          const value = item instanceof HTMLElement ? item.dataset.value : undefined;
          if (!value) return;
          const optionIndex = parseInt((button instanceof HTMLElement ? button.dataset.optionIndex : '') || '0', 10);
          const optionName = (button instanceof HTMLElement ? button.dataset.optionName : '') || '';

          // Update selected value
          input.value = value;

          // Update button display
          // First option: Show swatch image + value
          // Other options: Show "Option Name: Value" format
          const selectedContainer = button.querySelector('.bmsm__custom-dropdown-selected');
          if (!selectedContainer) return;

          // Get swatch image URL from selected item (only for first option)
          const swatchImageUrl = optionIndex === 0 && item instanceof HTMLElement && item.dataset.image && item.dataset.image.trim() !== '' ? item.dataset.image.trim() : null;

          // Update or create swatch preview for first option
          if (optionIndex === 0 && swatchImageUrl && swatchImageUrl !== '') {
            let swatchPreview = selectedContainer.querySelector('.bmsm__swatch-preview');
            if (!swatchPreview) {
              swatchPreview = document.createElement('span');
              swatchPreview.className = 'bmsm__swatch-preview';
              const textEl = selectedContainer.querySelector('.bmsm__custom-dropdown-text');
              if (textEl) {
                selectedContainer.insertBefore(swatchPreview, textEl);
              }
            }
            // Update swatch image
            let swatchImg = swatchPreview.querySelector('img');
            if (!swatchImg) {
              swatchImg = document.createElement('img');
              swatchImg.alt = value;
              swatchImg.width = 24;
              swatchImg.height = 24;
              swatchImg.loading = 'lazy';
              swatchPreview.appendChild(swatchImg);
            }
            swatchImg.src = swatchImageUrl;
            swatchImg.alt = value;
          } else {
            // Remove swatch preview for non-first options
            const swatchPreview = selectedContainer.querySelector('.bmsm__swatch-preview');
            if (swatchPreview) {
              swatchPreview.remove();
            }
          }

          // Update text content
          const textEl = selectedContainer.querySelector('.bmsm__custom-dropdown-text');
          if (textEl) {
            if (optionIndex === 0) {
              // First option: Show only value (swatch image is separate) - make it bold
              textEl.innerHTML = `<span class="bmsm__selected-value">${value}</span>`;
            } else {
              // Other options: Show "Option Name: Value" format - only value is bold
              textEl.innerHTML = optionName ? `${optionName}: <span class="bmsm__selected-value">${value}</span>` : `<span class="bmsm__selected-value">${value}</span>`;
            }
          }

          // Update selected state
          for (const i of items) {
            i.classList.remove('bmsm__custom-dropdown-item--selected');
          }
          item.classList.add('bmsm__custom-dropdown-item--selected');

          // Close dropdown
          this.#closeCustomDropdown(button, list);

          // Trigger variant change
          const row = parseInt((button instanceof HTMLElement ? button.dataset.row : '') || '1', 10);
          const tier = parseInt((button instanceof HTMLElement ? button.dataset.tier : '') || '3', 10);

          // Update selections
          if (!this.#selections[tier]?.[row]) {
            if (!this.#selections[tier]) {
              this.#selections[tier] = {};
            }
            this.#selections[tier][row] = {};
          }
          if (this.#selections[tier]?.[row]) {
            this.#selections[tier][row][optionIndex] = value;
          }

          // Handle custom dropdown selection change (includes price update)
          this.#handleCustomDropdownChange(tier, row, optionIndex, value);
        });
      }

      // Close dropdown when clicking outside
      document.addEventListener('click', (e) => {
        if (e.target instanceof Node && !wrapper.contains(e.target)) {
          this.#closeCustomDropdown(button, list);
        }
      }, { signal: this.#abortController.signal });
    }
  }

  /**
   * Toggle custom dropdown
   * @param {HTMLButtonElement} button - Dropdown button
   * @param {HTMLUListElement} list - Dropdown list
   */
  #toggleCustomDropdown(button, list) {
    const isExpanded = button.getAttribute('aria-expanded') === 'true';
    
    if (isExpanded) {
      this.#closeCustomDropdown(button, list);
    } else {
      // Close all other dropdowns first
      this.#closeAllCustomDropdowns();
      this.#openCustomDropdown(button, list);
    }
  }

  /**
   * Open custom dropdown
   * @param {HTMLButtonElement} button - Dropdown button
   * @param {HTMLUListElement} list - Dropdown list
   */
  #openCustomDropdown(button, list) {
    button.setAttribute('aria-expanded', 'true');
    list.removeAttribute('hidden');
    
    // Focus first enabled item
    const firstEnabled = list.querySelector('.bmsm__custom-dropdown-item:not(.bmsm__custom-dropdown-item--disabled)');
    if (firstEnabled instanceof HTMLElement) {
      firstEnabled.focus();
    }
  }

  /**
   * Close custom dropdown
   * @param {HTMLButtonElement} button - Dropdown button
   * @param {HTMLUListElement} list - Dropdown list
   */
  #closeCustomDropdown(button, list) {
    button.setAttribute('aria-expanded', 'false');
    list.setAttribute('hidden', '');
  }

  /**
   * Close all custom dropdowns
   */
  #closeAllCustomDropdowns() {
    const allButtons = this.querySelectorAll('.bmsm__custom-dropdown-button[aria-expanded="true"]');

    for (const button of allButtons) {
      if (!(button instanceof HTMLButtonElement)) continue;

      const wrapper = button.closest('.bmsm__custom-dropdown-wrapper');
      const list = wrapper?.querySelector('.bmsm__custom-dropdown-list');

      if (list instanceof HTMLUListElement) {
        this.#closeCustomDropdown(button, list);
      }
    }
  }

  /**
   * Handle custom dropdown selection change
   * @param {number} tier - Current tier
   * @param {number} row - Row number
   * @param {number} optionIndex - Option index
   * @param {string} value - Selected value
   */
  #handleCustomDropdownChange(tier, row, optionIndex, value) {
    // Mark as manually interacted
    this.#hasManuallyInteracted = true;

    // Store selection
    if (!this.#selections[tier]?.[row]) {
      if (!this.#selections[tier]) {
        this.#selections[tier] = {};
      }
      this.#selections[tier][row] = {};
    }
    if (this.#selections[tier]?.[row]) {
      this.#selections[tier][row][optionIndex] = value;
    }

    // Auto-fill logic for size option (first time only)
    if (
      !this.#hasAutoFilled &&
      row === 1 &&
      optionIndex === this.#sizeOptionIndex &&
      this.#hasSizeOption &&
      this.#options.length > 1 // Only if there are multiple options
    ) {
      this.#autoFillRemainingRows(tier, 2, value);
      this.#hasAutoFilled = true;
    }

    // Update variant availability based on current selections
    this.#updateVariantAvailability();

    // Update add to cart button state
    this.#updateButtonState(tier);

    // Update prices if all variants are selected
    this.#updatePricesForSelectedVariants(tier);
  }

  /**
   * Listen to theme variant events (variant:update, variant:selected)
   */
  #listenToVariantEvents() {
    const { signal } = this.#abortController;

    // Listen for variant update events from PDP variant picker
    document.addEventListener(
      ThemeEvents.variantUpdate,
      /** @param {CustomEvent} event */ (event) => {
        const detail = event.detail;
        if (!detail?.resource) return;

        // Check if this is for our product
        const productId = this.dataset.productId;
        const eventProductId = detail.data?.productId;
        if (productId && eventProductId && String(productId) !== String(eventProductId)) {
          return;
        }

        const variant = detail.resource;

        // Only update prices from PDP variant if user hasn't interacted with BMSM dropdowns
        if (!this.#hasManuallyInteracted) {
          // Check if any tier has all variants selected - if so, use combined price for that tier
          let hasAllSelectedTier = false;
          
          for (const tier of this.#availableTiers) {
            const combinedPrice = this.#calculateCombinedPriceForTier(tier);
            if (combinedPrice !== null) {
              // This tier has all variants selected - recalculate its price
              this.#updatePricesForSelectedVariants(tier);
              hasAllSelectedTier = true;
            }
          }
          
          // Only update base prices if no tier has all variants selected
          if (!hasAllSelectedTier) {
            // Update prices based on new variant
            if (variant && variant.available && variant.price) {
              this.#updatePricesForBasePrice(variant.price);
            } else {
              // Variant is OOS, use product default price
              this.#updatePricesForBasePrice(this.#basePrice);
            }
          }

          // Handle size auto-fill if applicable
          if (this.#hasSizeOption && this.#sizeOptionIndex !== null) {
            // Only process if this is for size option
            const selectedSize = variant.option1 || variant.option2 || variant.option3;
            if (!selectedSize) return;

            // Get the option position from variant
            if (variant.options?.[this.#sizeOptionIndex]) {
              const sizeValue = variant.options[this.#sizeOptionIndex];
              this.#autoFillSizeFromPDP(sizeValue);
            }
          }
        }
      },
      { signal }
    );

    // Also listen for variant selected events
    document.addEventListener(
      ThemeEvents.variantSelected,
      /** @param {CustomEvent} event */ (event) => {
        const detail = event.detail;
        if (!detail?.resource) return;

        // Find selected variant
        const selectedVariantId = detail.resource.id;
        const selectedVariant = this.#variants.find((v) => String(v.id) === String(selectedVariantId));

        // Only update prices from PDP variant if user hasn't interacted with BMSM dropdowns
        if (!this.#hasManuallyInteracted && selectedVariant) {
          // Check if any tier has all variants selected - if so, use combined price for that tier
          let hasAllSelectedTier = false;
          
          for (const tier of this.#availableTiers) {
            const combinedPrice = this.#calculateCombinedPriceForTier(tier);
            if (combinedPrice !== null) {
              // This tier has all variants selected - recalculate its price
              this.#updatePricesForSelectedVariants(tier);
              hasAllSelectedTier = true;
            }
          }
          
          // Only update base prices if no tier has all variants selected
          if (!hasAllSelectedTier) {
            // Update prices based on new variant
            if (selectedVariant.available && selectedVariant.price) {
              this.#updatePricesForBasePrice(selectedVariant.price);
            } else {
              // Variant is OOS, use product default price
              this.#updatePricesForBasePrice(this.#basePrice);
            }
          }

          // Handle size auto-fill if applicable
          if (this.#hasSizeOption && this.#sizeOptionIndex !== null) {
            const sizeValue = selectedVariant.options?.[this.#sizeOptionIndex];
            if (typeof sizeValue === 'string') {
              this.#autoFillSizeFromPDP(sizeValue);
            }
          }
        }
      },
      { signal }
    );
  }

  /**
   * Auto-fill size selection from PDP swatch
   * @param {string} size - Selected size value
   */
  #autoFillSizeFromPDP(size) {
    // Don't auto-fill if user has manually interacted with BMSM dropdowns or if no size option
    // Allow re-auto-filling if user hasn't manually interacted (removed #hasAutoFilled check)
    if (this.#hasManuallyInteracted || this.#sizeOptionIndex === null) return;

    const tier = this.#currentTier;
    const container = this.querySelector(`.bmsm__tier[data-tier="${tier}"].bmsm__tier--selected .bmsm__variants-container`);
    if (!container) return;

    for (let row = 1; row <= tier; row++) {
      // Handle custom dropdown (all options now use custom dropdowns)
      const customDropdown = container.querySelector(`.bmsm__custom-dropdown-wrapper[data-row="${row}"][data-option-index="${this.#sizeOptionIndex}"][data-tier="${tier}"]`);
      if (customDropdown) {
        const input = customDropdown.querySelector('.bmsm__custom-dropdown-input');
        const button = customDropdown.querySelector('.bmsm__custom-dropdown-button');
        const list = customDropdown.querySelector('.bmsm__custom-dropdown-list');
        const item = list?.querySelector(`.bmsm__custom-dropdown-item[data-value="${size}"]`);
        
        if (item && !item.classList.contains('bmsm__custom-dropdown-item--disabled')) {
          // Update input value
          if (input instanceof HTMLInputElement) {
            input.value = size;
          }

          // Update button display
          // First option: Show swatch image + value
          // Other options: Show "Option Name: Value" format
          if (button instanceof HTMLElement) {
            const selectedContainer = button.querySelector('.bmsm__custom-dropdown-selected');
            if (!selectedContainer) return;

            const optionName = button.dataset.optionName || '';
            const optionIndex = parseInt(button.dataset.optionIndex || '0', 10);

            // Get swatch image URL from selected item (only for first option)
            const swatchImageUrl = optionIndex === 0 && item instanceof HTMLElement && item.dataset.image && item.dataset.image.trim() !== '' ? item.dataset.image.trim() : null;

            // Update or create swatch preview for first option
            if (optionIndex === 0 && swatchImageUrl && swatchImageUrl !== '') {
              let swatchPreview = selectedContainer.querySelector('.bmsm__swatch-preview');
              if (!swatchPreview) {
                swatchPreview = document.createElement('span');
                swatchPreview.className = 'bmsm__swatch-preview';
                const textEl = selectedContainer.querySelector('.bmsm__custom-dropdown-text');
                if (textEl) {
                  selectedContainer.insertBefore(swatchPreview, textEl);
                }
              }
              // Update swatch image
              let swatchImg = swatchPreview.querySelector('img');
              if (!swatchImg) {
                swatchImg = document.createElement('img');
                swatchImg.alt = size;
                swatchImg.width = 24;
                swatchImg.height = 24;
                swatchImg.loading = 'lazy';
                swatchPreview.appendChild(swatchImg);
              }
              swatchImg.src = swatchImageUrl;
              swatchImg.alt = size;
            } else {
              // Remove swatch preview for non-first options
              const swatchPreview = selectedContainer.querySelector('.bmsm__swatch-preview');
              if (swatchPreview) {
                swatchPreview.remove();
              }
            }

            // Update text content
            const textEl = selectedContainer.querySelector('.bmsm__custom-dropdown-text');
            if (textEl) {
              if (optionIndex === 0) {
                // First option: Show only value (swatch image is separate) - make it bold
                textEl.innerHTML = `<span class="bmsm__selected-value">${size}</span>`;
              } else {
                // Other options: Show "Option Name: Value" format - only value is bold
                textEl.innerHTML = optionName ? `${optionName}: <span class="bmsm__selected-value">${size}</span>` : `<span class="bmsm__selected-value">${size}</span>`;
              }
            }
          }

          // Update selected state
          const allItems = list?.querySelectorAll('.bmsm__custom-dropdown-item');

          if (allItems) {
            for (const i of allItems) {
              i.classList.remove('bmsm__custom-dropdown-item--selected');
            }
          }
          item.classList.add('bmsm__custom-dropdown-item--selected');

          // Update selections - preserve existing selections for other options
          if (this.#sizeOptionIndex !== null) {
            // Ensure tier and row selections objects exist
            if (!this.#selections[tier]) {
              this.#selections[tier] = {};
            }
            if (!this.#selections[tier][row]) {
              this.#selections[tier][row] = {};
            }
            
            // Only update the size option, preserve all other option selections
            const rowSelections = this.#selections[tier][row];
            if (rowSelections) {
              rowSelections[this.#sizeOptionIndex] = size;
            }
          }
        }
      }
    }

    // Update variant availability after auto-fill
    this.#updateVariantAvailability();
    
    // Update button state
    this.#updateButtonState(tier);
    
    // Recalculate prices if all variants are now selected after auto-fill
    this.#updatePricesForSelectedVariants(tier);
  }

  /**
   * Handle tier selection change
   * @param {Event} event - Change event from radio button
   */
  handleTierChange(event) {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;

    const newTier = parseInt(target.value, 10);
    const previousTier = this.#currentTier;

    // Update current tier first
    this.#currentTier = newTier;

    // Update UI classes for selected tier
    this.#updateTierUI(newTier);

    // Persist selections when switching tiers
    this.#persistSelectionsOnTierChange(previousTier, newTier);

    // Update variant dropdowns for new tier
    this.#buildVariantDropdowns(newTier);

    // Update variant availability
    this.#updateVariantAvailability();

    // Update add to cart button state
    this.#updateButtonState(newTier);

    // Update prices based on selected variants in new tier
    // Use requestAnimationFrame to ensure selections are fully persisted and DOM is updated
    requestAnimationFrame(() => {
      this.#updatePricesForSelectedVariants(newTier);
    });
  }

  /**
   * Update tier UI (selected state)
   * @param {number} tier - Selected tier number
   */
  #updateTierUI(tier) {
    const tiers = this.querySelectorAll('.bmsm__tier');

    for (const tierEl of tiers) {
      if (!(tierEl instanceof HTMLElement)) continue;
      const tierValue = tierEl.dataset.tier;
      tierEl.classList.toggle('bmsm__tier--selected', tierValue === String(tier));
    }
  }

  /**
   * Find the tier with the most complete selections that can be used as source
   * @param {number} targetTier - Target tier we're moving to (only check tiers less than this)
   * @returns {number|null} Tier number with most complete selections, or null
   */
  #findBestSourceTier(targetTier) {
    let bestTier = null;
    let bestCompleteCount = 0;
    let bestRowCount = 0;
    let bestSelectionCount = 0;

    // Check all available tiers that are less than targetTier to find the one with most complete selections
    for (const tier of this.#availableTiers) {
      // Only consider tiers that are less than the target tier
      if (tier >= targetTier) continue;

      const tierSelections = this.#selections[tier];
      if (!tierSelections) continue;

      // Count how many complete rows this tier has
      let completeRows = 0;
      // Count how many rows have at least one selection
      let rowsWithSelections = 0;
      // Count total number of selections across all rows
      let totalSelections = 0;

      for (let row = 1; row <= tier; row++) {
        const rowSelections = tierSelections[row];
        if (!rowSelections) continue;

        // Check if all options are selected for this row
        let isComplete = true;
        let hasAnySelection = false;
        for (let optIdx = 0; optIdx < this.#options.length; optIdx++) {
          const value = rowSelections[optIdx];
          if (value && value !== '') {
            hasAnySelection = true;
            totalSelections++;
          } else {
            isComplete = false;
          }
        }

        if (isComplete) {
          completeRows++;
        }
        if (hasAnySelection) {
          rowsWithSelections++;
        }
      }

      // Priority 1: Prefer tier with more complete rows
      // Priority 2: If same complete rows, prefer tier with more rows that have selections
      // Priority 3: If same row counts, prefer tier with more total selections
      // Priority 4: If all equal, prefer higher tier number (more recent)
      const isBetter =
        completeRows > bestCompleteCount ||
        (completeRows === bestCompleteCount && rowsWithSelections > bestRowCount) ||
        (completeRows === bestCompleteCount && rowsWithSelections === bestRowCount && totalSelections > bestSelectionCount) ||
        (completeRows === bestCompleteCount && rowsWithSelections === bestRowCount && totalSelections === bestSelectionCount && rowsWithSelections > 0 && tier > (bestTier || 0));

      if (isBetter) {
        bestCompleteCount = completeRows;
        bestRowCount = rowsWithSelections;
        bestSelectionCount = totalSelections;
        bestTier = tier;
      }
    }

    return bestTier;
  }

  /**
   * Persist selections when switching between tiers
   * @param {number} fromTier - Previous tier
   * @param {number} toTier - New tier
   */
  #persistSelectionsOnTierChange(fromTier, toTier) {
    // Ensure target tier selections object exists
    if (!this.#selections[toTier]) {
      this.#selections[toTier] = {};
    }

    // Lower to higher: find the best source tier with most complete selections
    if (toTier > fromTier) {
      // Check how many complete rows the target tier currently has
      let targetCompleteRows = 0;
      const targetSelections = this.#selections[toTier];
      if (targetSelections) {
        for (let row = 1; row <= toTier; row++) {
          const rowSelections = targetSelections[row];
          if (rowSelections) {
            let isComplete = true;
            for (let optIdx = 0; optIdx < this.#options.length; optIdx++) {
              const value = rowSelections[optIdx];
              if (!value || value === '') {
                isComplete = false;
                break;
              }
            }
            if (isComplete) {
              targetCompleteRows++;
            }
          }
        }
      }
      
      // Find the tier with the most complete selections among all tiers less than toTier
      const bestSourceTier = this.#findBestSourceTier(toTier);
      
      // Count complete rows in best source tier
      let sourceCompleteRows = 0;
      if (bestSourceTier) {
        const sourceSelections = this.#selections[bestSourceTier];
        if (sourceSelections) {
          for (let row = 1; row <= bestSourceTier; row++) {
            const rowSelections = sourceSelections[row];
            if (rowSelections) {
              let isComplete = true;
              for (let optIdx = 0; optIdx < this.#options.length; optIdx++) {
                const value = rowSelections[optIdx];
                if (!value || value === '') {
                  isComplete = false;
                  break;
                }
              }
              if (isComplete) {
                sourceCompleteRows++;
              }
            }
          }
        }
      }
      
      // If no best source tier found, use fromTier as fallback
      const sourceTier = bestSourceTier || fromTier;
      
      // Only overwrite if source has more complete rows than target
      if (sourceCompleteRows <= targetCompleteRows && targetCompleteRows > 0) {
        // Keep existing target selections (more complete than source)
        return;
      }
      
      // Clear existing selections for target tier to ensure clean copy
      this.#selections[toTier] = {};
      
      // Copy selections from the best source tier
      for (let row = 1; row <= sourceTier; row++) {
        const sourceRow = this.#selections[sourceTier]?.[row];
        if (sourceRow) {
          // Deep copy ALL option values from source row to ensure complete selection
          const targetRow = /** @type {{ [x: number]: string }} */ ({});
          this.#selections[toTier][row] = targetRow;
          
          // Copy each option value with numeric keys (0, 1, 2, etc.)
          for (let optIdx = 0; optIdx < this.#options.length; optIdx++) {
            const value = sourceRow[optIdx];
            if (value !== undefined) {
              targetRow[optIdx] = value;
            }
          }
        }
      }

      // Auto-fill new rows if we haven't manually interacted and have size option
      if (!this.#hasManuallyInteracted && this.#hasSizeOption && this.#sizeOptionIndex !== null) {
        const firstRowSize = this.#selections[sourceTier]?.[1]?.[this.#sizeOptionIndex];
        if (typeof firstRowSize === 'string' && firstRowSize !== '') {
          // Auto-fill remaining rows with the first row's size
          this.#autoFillRemainingRows(toTier, sourceTier + 1, firstRowSize);
        }
      }
    }
    // Higher to lower: keep only first N selections
    else if (toTier < fromTier) {
      // Clear existing selections for target tier to ensure clean copy
      this.#selections[toTier] = {};
      
      for (let row = 1; row <= toTier; row++) {
        const sourceRow = this.#selections[fromTier]?.[row];
        if (sourceRow) {
          // Deep copy ALL option values from source row to ensure complete selection
          const targetRow = /** @type {{ [x: number]: string }} */ ({});
          this.#selections[toTier][row] = targetRow;
          
          // Copy each option value with numeric keys (0, 1, 2, etc.)
          // Copy all values, including empty strings, to preserve selection state
          for (let optIdx = 0; optIdx < this.#options.length; optIdx++) {
            const value = sourceRow[optIdx];
            // Copy the value as-is (could be string, undefined, null, or empty string)
            if (value !== undefined) {
              targetRow[optIdx] = value;
            }
          }
        }
      }
    }
  }

  /**
   * Build variant dropdown containers for a tier
   * @param {number} tier - Tier number
   */
  #buildVariantDropdowns(tier) {
    const container = this.querySelector(`.bmsm__tier[data-tier="${tier}"] .bmsm__variants-container`);
    if (!container) return;

    // Update dropdowns with persisted selections (all options now use custom dropdowns)
    const tierSelections = this.#selections[tier];
    if (!tierSelections) return;

    for (let row = 1; row <= tier; row++) {
      for (let optIdx = 0; optIdx < this.#options.length; optIdx++) {
        const savedValue = tierSelections[row]?.[optIdx] || '';
        
        const customDropdown = container.querySelector(`.bmsm__custom-dropdown-wrapper[data-row="${row}"][data-option-index="${optIdx}"][data-tier="${tier}"]`);
        if (customDropdown && savedValue) {
          const input = customDropdown.querySelector('.bmsm__custom-dropdown-input');
          const button = customDropdown.querySelector('.bmsm__custom-dropdown-button');
          const list = customDropdown.querySelector('.bmsm__custom-dropdown-list');
          const item = list?.querySelector(`.bmsm__custom-dropdown-item[data-value="${savedValue}"]`);
          
          if (input instanceof HTMLInputElement) {
            input.value = savedValue;
          }
          if (item && button instanceof HTMLElement) {
            const selectedContainer = button.querySelector('.bmsm__custom-dropdown-selected');
            if (!selectedContainer) continue;

            const optionName = button.dataset.optionName || '';
            const optionIndex = parseInt(button.dataset.optionIndex || '0', 10);

            // Get swatch image URL from selected item (only for first option)
            const swatchImageUrl = optionIndex === 0 && item instanceof HTMLElement && item.dataset.image && item.dataset.image.trim() !== '' ? item.dataset.image.trim() : null;

            // Update or create swatch preview for first option
            if (optionIndex === 0 && swatchImageUrl && swatchImageUrl !== '') {
              let swatchPreview = selectedContainer.querySelector('.bmsm__swatch-preview');
              if (!swatchPreview) {
                swatchPreview = document.createElement('span');
                swatchPreview.className = 'bmsm__swatch-preview';
                const textEl = selectedContainer.querySelector('.bmsm__custom-dropdown-text');
                if (textEl) {
                  selectedContainer.insertBefore(swatchPreview, textEl);
                }
              }
              // Update swatch image
              let swatchImg = swatchPreview.querySelector('img');
              if (!swatchImg) {
                swatchImg = document.createElement('img');
                swatchImg.alt = savedValue;
                swatchImg.width = 24;
                swatchImg.height = 24;
                swatchImg.loading = 'lazy';
                swatchPreview.appendChild(swatchImg);
              }
              swatchImg.src = swatchImageUrl;
              swatchImg.alt = savedValue;
            } else {
              // Remove swatch preview for non-first options
              const swatchPreview = selectedContainer.querySelector('.bmsm__swatch-preview');
              if (swatchPreview) {
                swatchPreview.remove();
              }
            }

            // Update text content
            const textEl = selectedContainer.querySelector('.bmsm__custom-dropdown-text');
            if (textEl) {
              if (optionIndex === 0) {
                // First option: Show only value (swatch image is separate) - make it bold
                textEl.innerHTML = `<span class="bmsm__selected-value">${savedValue}</span>`;
              } else {
                // Other options: Show "Option Name: Value" format - only value is bold
                textEl.innerHTML = optionName ? `${optionName}: <span class="bmsm__selected-value">${savedValue}</span>` : `<span class="bmsm__selected-value">${savedValue}</span>`;
              }
            }
            
            // Update selected state
            const allItems = list?.querySelectorAll('.bmsm__custom-dropdown-item');
            allItems?.forEach((i) => {
              i.classList.remove('bmsm__custom-dropdown-item--selected');
            });
            item.classList.add('bmsm__custom-dropdown-item--selected');
          }
        }
      }
    }

    this.#updateVariantAvailability();
  }

  /**
   * Auto-fill remaining rows with size selection
   * @param {number} tier - Current tier
   * @param {number} startRow - Starting row for auto-fill
   * @param {string} size - Size value to fill
   */
  #autoFillRemainingRows(tier, startRow, size) {
    if (!this.#hasSizeOption || this.#sizeOptionIndex === null) return;

    const container = this.querySelector(`.bmsm__tier[data-tier="${tier}"].bmsm__tier--selected .bmsm__variants-container`);
    if (!container) return;

    // Calculate total available stock for this size across all variants
    let totalSizeStock = 0;

    for (const v of this.#variants) {
      if (v.options?.[this.#sizeOptionIndex] !== size || !v.available) continue;

      const inventoryInfo = this.#variantInventory.get(v.id);
      if (!inventoryInfo || !inventoryInfo.available) continue;

      totalSizeStock += inventoryInfo.inventory;
    }

    // Track which variants with this size have been used
    const variantUsageCount = new Map();

    // Count how many times the selected size has been used in previous rows
    if (this.#sizeOptionIndex !== null) {
      for (let prevRow = 1; prevRow < startRow; prevRow++) {
        const prevSize = this.#selections[tier]?.[prevRow]?.[this.#sizeOptionIndex];

        if (prevSize === size) {
          // Find the variant used in previous row and increment its count
          const prevVariant = this.#findVariantForSelections(tier, prevRow);

          if (prevVariant) {
            const currentCount = variantUsageCount.get(prevVariant.id) || 0;
            variantUsageCount.set(prevVariant.id, currentCount + 1);
          }
        }
      }
    }

    // Count how many times this size has been used so far
    let usedSizeCount = 0;

    for (const count of variantUsageCount.values()) {
      usedSizeCount += count;
    }

    for (let row = startRow; row <= tier; row++) {
      // Check if we've used up all available stock for this size
      if (usedSizeCount >= totalSizeStock) {
        // Stop auto-fill if total stock limit reached for this size
        break;
      }

      // Check if we can add another variant with this size (respecting individual variant stock)
      const canAdd = this.#canAddVariantWithSize(tier, row, size, variantUsageCount);

      if (!canAdd) {
        // Stop auto-fill if stock limit reached for all variants with this size
        // Don't fill this row - let user select a different size
        break;
      }

      // Find the best variant to use (one that still has stock)
      const bestVariant = this.#findBestVariantForSize(tier, row, size, variantUsageCount);
      if (!bestVariant) {
        // No variant available with this size - stop auto-fill
        break;
      }

      // Increment usage count for this variant
      const currentCount = variantUsageCount.get(bestVariant.id) || 0;
      variantUsageCount.set(bestVariant.id, currentCount + 1);
      usedSizeCount++;

      // Handle custom dropdown (all options now use custom dropdowns)
      const customDropdown = container.querySelector(`.bmsm__custom-dropdown-wrapper[data-row="${row}"][data-option-index="${this.#sizeOptionIndex}"][data-tier="${tier}"]`);
      if (customDropdown) {
        const input = customDropdown.querySelector('.bmsm__custom-dropdown-input');
        const button = customDropdown.querySelector('.bmsm__custom-dropdown-button');
        const list = customDropdown.querySelector('.bmsm__custom-dropdown-list');
        const item = list?.querySelector(`.bmsm__custom-dropdown-item[data-value="${size}"]`);
        
        if (input instanceof HTMLInputElement) {
          input.value = size;
        }
        if (item && button instanceof HTMLElement) {
          const selectedContainer = button.querySelector('.bmsm__custom-dropdown-selected');
          if (!selectedContainer) continue;

          const optionName = button.dataset.optionName || '';
          const optionIndex = parseInt(button.dataset.optionIndex || '0', 10);

          // Get swatch image URL from selected item (only for first option)
          const swatchImageUrl = optionIndex === 0 && item instanceof HTMLElement ? item.dataset.image : null;

          // Update or create swatch preview for first option
          if (optionIndex === 0 && swatchImageUrl) {
            let swatchPreview = selectedContainer.querySelector('.bmsm__swatch-preview');
            if (!swatchPreview) {
              swatchPreview = document.createElement('span');
              swatchPreview.className = 'bmsm__swatch-preview';
              const textEl = selectedContainer.querySelector('.bmsm__custom-dropdown-text');
              if (textEl) {
                selectedContainer.insertBefore(swatchPreview, textEl);
              }
            }
            // Update swatch image
            let swatchImg = swatchPreview.querySelector('img');
            if (!swatchImg) {
              swatchImg = document.createElement('img');
              swatchImg.alt = size;
              swatchImg.width = 24;
              swatchImg.height = 24;
              swatchImg.loading = 'lazy';
              swatchPreview.appendChild(swatchImg);
            }
            swatchImg.src = swatchImageUrl;
            swatchImg.alt = size;
          } else {
            // Remove swatch preview for non-first options
            const swatchPreview = selectedContainer.querySelector('.bmsm__swatch-preview');
            if (swatchPreview) {
              swatchPreview.remove();
            }
          }

          // Update text content
          const textEl = selectedContainer.querySelector('.bmsm__custom-dropdown-text');
          if (textEl) {
            if (optionIndex === 0) {
              // First option: Show only value (swatch image is separate) - make it bold
              textEl.innerHTML = `<span class="bmsm__selected-value">${size}</span>`;
            } else {
              // Other options: Show "Option Name: Value" format - only value is bold
              textEl.innerHTML = optionName ? `${optionName}: <span class="bmsm__selected-value">${size}</span>` : `<span class="bmsm__selected-value">${size}</span>`;
            }
          }
          
          // Update selected state
          const allItems = list?.querySelectorAll('.bmsm__custom-dropdown-item');

          if (allItems) {
            for (const i of allItems) {
              i.classList.remove('bmsm__custom-dropdown-item--selected');
            }
          }
          item.classList.add('bmsm__custom-dropdown-item--selected');
        }
      }

      if (this.#sizeOptionIndex !== null) {
        if (!this.#selections[tier]?.[row]) {
          if (!this.#selections[tier]) {
            this.#selections[tier] = {};
          }
          this.#selections[tier][row] = {};
        }
        if (this.#sizeOptionIndex !== null) {
          const rowSelections = this.#selections[tier]?.[row];
          if (rowSelections) {
            rowSelections[this.#sizeOptionIndex] = size;
          }
        }
      }
    }

    // Update availability after auto-fill
    this.#updateVariantAvailability();
    this.#updateButtonState(tier);

    // Update prices if all variants are selected
    this.#updatePricesForSelectedVariants(tier);
  }

  /**
   * Find variant for given tier and row selections
   * @param {number} tier - Tier number
   * @param {number} row - Row number
   * @returns {VariantData|null}
   */
  #findVariantForSelections(tier, row) {
    const selections = this.#selections[tier]?.[row];
    if (!selections) return null;

    // Build option values array
    /** @type {string[]} */
    const optionValues = [];
    for (let i = 0; i < this.#options.length; i++) {
      optionValues.push(selections[i] || '');
    }

    // Find matching variant
    return this.#variants.find((v) => {
      if (!v.options || v.options.length !== optionValues.length) return false;
      return v.options.every((opt, idx) => opt === optionValues[idx]);
    }) || null;
  }

  /**
   * Check if we can add another variant with given size (respecting individual variant stock)
   * @param {number} tier - Current tier
   * @param {number} row - Row to check
   * @param {string} size - Size value
   * @param {Map<number, number>} variantUsageCount - Map tracking how many times each variant has been used
   * @returns {boolean}
   */
  #canAddVariantWithSize(tier, row, size, variantUsageCount = new Map()) {
    if (this.#sizeOptionIndex === null) return true;

    // Find all variants with this size that have available stock
    for (const v of this.#variants) {
      if (v.options?.[this.#sizeOptionIndex] !== size || !v.available) continue;

      const inventoryInfo = this.#variantInventory.get(v.id);
      if (!inventoryInfo || !inventoryInfo.available) continue;

      const usedCount = variantUsageCount.get(v.id) || 0;

      if (usedCount < inventoryInfo.inventory) {
        return true;
      }
    }

    return false;
  }

  /**
   * Find the best variant to use for a given size
   * @param {number} tier - Current tier
   * @param {number} row - Row number
   * @param {string} size - Size value
   * @param {Map<number, number>} variantUsageCount - Map tracking variant usage
   * @returns {VariantData|null}
   */
  #findBestVariantForSize(tier, row, size, variantUsageCount) {
    if (this.#sizeOptionIndex === null) return null;

    // Find variants with this size that still have stock
    for (const v of this.#variants) {
      if (v.options?.[this.#sizeOptionIndex] !== size || !v.available) continue;

      const inventoryInfo = this.#variantInventory.get(v.id);
      if (!inventoryInfo || !inventoryInfo.available) continue;

      const usedCount = variantUsageCount.get(v.id) || 0;

      if (usedCount < inventoryInfo.inventory) {
        return v;
      }
    }

    return null;
  }

  /**
   * Update variant availability based on current selections
   */
  #updateVariantAvailability() {
    const tier = this.#currentTier;
    const container = this.querySelector(`.bmsm__tier[data-tier="${tier}"].bmsm__tier--selected .bmsm__variants-container`);
    if (!container) return;

    // Track selected variant IDs to update stock counts
    const selectedVariants = this.#getSelectedVariantCounts(tier);

    // Update all dropdowns for current tier (all options now use custom dropdowns)
    for (let row = 1; row <= tier; row++) {
      for (let optIdx = 0; optIdx < this.#options.length; optIdx++) {
        const customDropdown = container.querySelector(`.bmsm__custom-dropdown-wrapper[data-row="${row}"][data-option-index="${optIdx}"][data-tier="${tier}"]`);
        if (customDropdown) {
          const list = customDropdown.querySelector('.bmsm__custom-dropdown-list');
          const items = list?.querySelectorAll('.bmsm__custom-dropdown-item');
          
          if (!items) continue;

          for (const item of items) {
            if (!(item instanceof HTMLElement)) continue;
            const value = item.dataset.value;

            if (!value) continue;

            // Check if this option combination is available
            const isAvailable = this.#isOptionAvailable(tier, row, optIdx, value, selectedVariants);

            if (!isAvailable) {
              item.classList.add('bmsm__custom-dropdown-item--disabled');
              const textEl = item.querySelector('.bmsm__custom-dropdown-item-text');

              if (textEl && !textEl.textContent.includes('(Out of Stock)')) {
                textEl.textContent = `${value} (Out of Stock)`;
              }
            } else {
              item.classList.remove('bmsm__custom-dropdown-item--disabled');
              const textEl = item.querySelector('.bmsm__custom-dropdown-item-text');

              if (textEl && textEl.textContent.includes('(Out of Stock)')) {
                textEl.textContent = value;
              }
            }
          }
        }
      }
    }
  }

  /**
   * Get count of selected variants
   * @param {number} tier - Current tier
   * @returns {Map<number, number>}
   */
  #getSelectedVariantCounts(tier) {
    const counts = new Map();

    for (let row = 1; row <= tier; row++) {
      const variant = this.#findVariantFromSelection(tier, row);
      if (variant) {
        const current = counts.get(variant.id) || 0;
        counts.set(variant.id, current + 1);
      }
    }

    return counts;
  }

  /**
   * Check if an option value is available for selection
   * @param {number} tier - Current tier
   * @param {number} row - Current row
   * @param {number} optionIndex - Option index
   * @param {string} value - Option value
   * @param {Map<number, number>} selectedCounts - Already selected variant counts
   * @returns {boolean}
   */
  #isOptionAvailable(tier, row, optionIndex, value, selectedCounts) {
    // Get current selections for this row (excluding the option we're checking)
    const currentSelections = { ...(this.#selections[tier]?.[row] || {}) };
    
    // Check if any variant has this option value and matches other selected options
    for (const variant of this.#variants) {
      // First check: variant must have the option value we're checking
      if (variant.options?.[optionIndex] !== value) {
        continue;
      }

      // Second check: variant must match any OTHER already-selected options
      let matchesOtherSelections = true;
      for (let idx = 0; idx < this.#options.length; idx++) {
        if (idx === optionIndex) continue; // Skip the option we're checking
        
        const selectedValue = currentSelections[idx];
        if (selectedValue && variant.options?.[idx] !== selectedValue) {
          matchesOtherSelections = false;
          break;
        }
      }

      if (!matchesOtherSelections) continue;

      // Third check: check inventory availability using pre-calculated data from Liquid
      const inventoryInfo = this.#variantInventory.get(variant.id);
      if (!inventoryInfo) {
        // If no inventory info, assume unavailable
        continue;
      }

      // If variant is explicitly not available, skip it
      if (inventoryInfo.available === false) {
        continue;
      }

      // Get how many times this variant is already selected in other rows
      const alreadySelected = selectedCounts.get(variant.id) || 0;
      
      // Get inventory from pre-calculated data
      const inventory = inventoryInfo.inventory;

      // Don't count current row's selection if it's the same variant
      const currentRowVariant = this.#findVariantFromSelection(tier, row);
      const adjustedCount = currentRowVariant?.id === variant.id ? Math.max(0, alreadySelected - 1) : alreadySelected;

      // If we haven't exceeded inventory, this option is available
      if (adjustedCount < inventory) {
        return true;
      }
    }

    return false;
  }

  /**
   * Find variant from current row selection
   * @param {number} tier - Current tier
   * @param {number} row - Row number
   * @returns {VariantData|null}
   */
  #findVariantFromSelection(tier, row) {
    const selections = this.#selections[tier]?.[row];
    if (!selections) return null;

    // Check if all options are selected (must be non-empty strings)
    for (let i = 0; i < this.#options.length; i++) {
      const selection = selections[i];
      if (selection === undefined || selection === null || selection === '') {
        return null;
      }
    }

    // Find matching variant
    return (
      this.#variants.find((variant) => {
        return this.#options.every((_, idx) => {
          return variant.options?.[idx] === selections[idx];
        });
      }) || null
    );
  }

  /**
   * Get all selected variant IDs for a tier
   * @param {number} tier - Current tier
   * @returns {number[]} Array of variant IDs
   */
  #getVariantsForTier(tier) {
    /** @type {number[]} */
    const variantIds = [];

    if (!this.#hasVariants) {
      // Single variant product - return default variant ID
      const defaultVariant = this.#variants[0];
      if (defaultVariant) {
        // Return array with tier quantity of the same variant ID
        return Array(tier).fill(defaultVariant.id);
      }
      return variantIds;
    }

    // Loop through all rows for this tier and collect variant IDs
    for (let row = 1; row <= tier; row++) {
      const variant = this.#findVariantFromSelection(tier, row);
      if (variant) {
        variantIds.push(variant.id);
      }
    }

    return variantIds;
  }
  /**
   * Update add to cart button state
   * @param {number} tier - Current tier
   */
  #getAddToCartElements(tier) {
    const button = this.querySelector(
      `[data-role="add-to-cart-button"][data-tier="${tier}"]`
    );
    const text = button?.querySelector('[data-role="add-to-cart-text"]');
    return { button, text };
  }

  /**
   * @param {number} tier
   */
  #updateButtonState(tier) {
    const { button } = this.#getAddToCartElements(tier);
    if (!(button instanceof HTMLButtonElement)) return;

    if (!this.#hasVariants) {
      button.disabled = false;
      return;
    }

    // Check if all selections are complete
    let allComplete = true;
    for (let row = 1; row <= tier; row++) {
      for (let optIdx = 0; optIdx < this.#options.length; optIdx++) {
        if (!this.#selections[tier]?.[row]?.[optIdx]) {
          allComplete = false;
          break;
        }
      }
      if (!allComplete) break;
    }

    button.disabled = !allComplete;
  }

  /**
   * Update all button states
   */
  #updateAllButtonStates() {
    for (const tier of this.#availableTiers) {
      this.#updateButtonState(tier);
    }
  }

  /**
   * Set up direct event listeners for add to cart buttons
   * This avoids issues with component nesting where on:click might target wrong component
   */
  #setupAddToCartListeners() {
    // Clean up existing listeners first
    this.#cleanupAddToCartListeners();
    
    // Use setTimeout to ensure DOM is fully ready (buttons might be in conditional blocks)
    setTimeout(() => {
      const buttons = this.querySelectorAll('button.bmsm__add-to-cart[data-tier]');

      for (const button of buttons) {
        if (!(button instanceof HTMLButtonElement)) continue;

        // Skip if already has listener (check by data attribute)
        if (button.dataset.bmsmListener === 'true') continue;

        button.dataset.bmsmListener = 'true';

        const handler = /** @param {MouseEvent} event */ (event) => {
          event.preventDefault();
          event.stopPropagation(); // Prevent add-to-cart-component from handling it
          this.handleAddToCart(event);
        };

        button.addEventListener('click', handler);
        this.#addToCartListeners.push({ button, handler });
      }
    }, 0);
  }

  /**
   * Clean up add to cart event listeners
   */
  #cleanupAddToCartListeners() {
    for (const { button, handler } of this.#addToCartListeners) {
      button.removeEventListener('click', handler);
      delete button.dataset.bmsmListener;
    }
    this.#addToCartListeners = [];
  }

  /**
   * Handle add to cart click
   * Uses native Shopify cart API following theme patterns
   * @param {MouseEvent} event - Click event
   */
  async handleAddToCart(event) {
    event.preventDefault();
    const currentTarget = event.currentTarget instanceof HTMLButtonElement ? event.currentTarget : null;
    const target = event.target instanceof Node ? event.target : null;
    const button = currentTarget || (target instanceof Element ? target.closest('button.bmsm__add-to-cart[data-tier]') : null);
    if (!(button instanceof HTMLButtonElement)) {
      console.error('Buy More Save More: Invalid button element', { event, currentTarget: event.currentTarget, target: event.target });
      return;
    }

    const tier = parseInt(button.dataset.tier || '3', 10);

    // Collect variant IDs for cart
    const cartItems = this.#collectCartItems(tier);
    if (cartItems.length === 0) {
      console.warn('Buy More Save More: No cart items to add', { tier, selections: this.#selections[tier] });
      return;
    }

    // Animate button itself
    this.#animateAddToCartButton(button);

    // Disable button
    button.disabled = true;

    try {
      // Get cart sections for updating cart UI (following native pattern)
      const cartItemsComponents = document.querySelectorAll('cart-items-component');
      /** @type {string[]} */
      const cartItemComponentsSectionIds = [];

      for (const item of cartItemsComponents) {
        if (item instanceof HTMLElement && item.dataset.sectionId) {
          cartItemComponentsSectionIds.push(item.dataset.sectionId);
        }
      }

      // Prepare request body following native pattern
      /** @type {{ items: Array<{id: number, quantity: number}>, sections?: string }} */
      const requestBody = {
        items: cartItems,
      };

      // Add sections if available
      if (cartItemComponentsSectionIds.length > 0) {
        requestBody.sections = cartItemComponentsSectionIds.join(',');
      }

      // Use fetchConfig helper like native implementation
      const fetchCfg = fetchConfig('json', { body: JSON.stringify(requestBody) });

      // Add items to cart using native Shopify cart API (following native pattern exactly)
      const response = await fetch(Theme.routes.cart_add_url, fetchCfg);
      const cartData = await response.json();
      // Check for errors in response (following native pattern - check status first)
      if (cartData.status) {
        // Error occurred - show error message
        const errorMessage = cartData.description || cartData.message || 'Failed to add to cart';
        
        // Show error above button
        const actionsContainer = button.closest('.bmsm__actions');
        if (actionsContainer) {
          const errorElement = actionsContainer.querySelector('[ref="addToCartTextError"]');
          if (errorElement) {
            errorElement.classList.remove('hidden');
            let errorText = errorElement.querySelector('.product-form-error-text');
            if (!errorText) {
              errorText = document.createElement('span');
              errorText.classList.add('product-form-error-text');
              errorElement.appendChild(errorText);
            }
            errorText.textContent = errorMessage;

            setTimeout(() => {
              errorElement.classList.add('hidden');
              if (errorText) {
                errorText.textContent = '';
              }
            }, 2000);
          }
        }

        // Reset button after error
        setTimeout(() => {
          button.disabled = false;
        }, 2000);
        
        return; // Exit early on error
      }

      // Success - calculate total item count
      const totalItemCount = cartItems.reduce((sum, item) => sum + item.quantity, 0);

      // Get first variant ID for sourceId (following native pattern)
      const firstVariantId = cartItems.length > 0 && cartItems[0] ? cartItems[0].id.toString() : this.id;

      // Dispatch cart add event using theme's event system (following native pattern exactly)
      // Native uses empty object {} as resource and variant ID as sourceId
      document.dispatchEvent(
        new CartAddEvent({}, firstVariantId, {
          source: 'buy-more-save-more',
          productId: this.dataset.productId,
          itemCount: totalItemCount,
          sections: cartData.sections || {},
        })
      );

      // Success - button animation is handled by add-to-cart-component via data-added attribute
      // No need to change text to "Added!" - checkmark animation will show

      // Reset button state after animation completes
      setTimeout(() => {
        button.disabled = false;
        this.#updateButtonState(tier); // Re-check button state
      }, 1000);
    } catch (error) {
      console.error('Add to cart error:', error);

      // Show error above button
      const actionsContainer = button.closest('.bmsm__actions');
      if (actionsContainer) {
        const errorElement = actionsContainer.querySelector('[ref="addToCartTextError"]');
        if (errorElement) {
          errorElement.classList.remove('hidden');
          let errorText = errorElement.querySelector('.product-form-error-text');
          if (!errorText) {
            errorText = document.createElement('span');
            errorText.classList.add('product-form-error-text');
            errorElement.appendChild(errorText);
          }
          errorText.textContent = error.message || 'Error - Try again';

          setTimeout(() => {
            errorElement.classList.add('hidden');
            if (errorText) {
              errorText.textContent = '';
            }
          }, 2000);
        }
      }

      // Reset button after error
      setTimeout(() => {
        button.disabled = false;
      }, 2000);
    }
  }

  /**
   * Collect cart items for add to cart
   * @param {number} tier - Current tier
   * @returns {Array<{id: number, quantity: number}>}
   */
  #collectCartItems(tier) {
    const items = [];

    if (!this.#hasVariants) {
      // Single variant - add default variant
      const defaultVariant = this.#variants[0];
      if (defaultVariant) {
        items.push({
          id: defaultVariant.id,
          quantity: tier,
        });
      }
      return items;
    }

    // Collect selected variants and group by ID
    const variantCounts = new Map();

    for (let row = 1; row <= tier; row++) {
      const variant = this.#findVariantFromSelection(tier, row);
      if (variant) {
        const current = variantCounts.get(variant.id) || 0;
        variantCounts.set(variant.id, current + 1);
      }
    }

    // Convert to cart items format
    for (const [variantId, quantity] of variantCounts) {
      items.push({ id: variantId, quantity });
    }

    return items;
  }


  /**
   * Animate the add to cart button itself
   * @param {HTMLButtonElement} button - The add to cart button
   */
  async #animateAddToCartButton(button) {
    // Clear all existing timeouts
    for (const timeoutId of this.#resetTimeouts) {
      clearTimeout(timeoutId);
    }
    this.#resetTimeouts = [];

    if (button.dataset.added !== 'true') {
      button.dataset.added = 'true';
    }

    await onAnimationEnd(button);

    // Create new timeout and store it in the array
    const timeoutId = setTimeout(() => {
      button.removeAttribute('data-added');

      // Remove this timeout from the array
      const index = this.#resetTimeouts.indexOf(timeoutId);

      if (index > -1) {
        this.#resetTimeouts.splice(index, 1);
      }
    }, 800);

    this.#resetTimeouts.push(timeoutId);
  }
}

if (!customElements.get('buy-more-save-more-component')) {
  customElements.define('buy-more-save-more-component', BuyMoreSaveMoreComponent);
}

